import { useFetcher, useLoaderData } from "@remix-run/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { HorizontalTable } from "~/components/atoms/Table";
import { Column, Row } from "~/components/atoms/Table/types";
import Typo from "~/components/atoms/Typo";
import { IUnitPrice } from "~/entities/unitPrice";
import { TProjectPriceListLoaderData } from "~/routes/($lang).loader-project-price-list";
import { IPaginateResponseData } from "~/types";
import { TProjectLoaderData } from "~/types/projectLoaderData";
import { cn } from "~/utilities/cn";
import { PAGE } from "~/utilities/constants/common";
import { EApiParam } from "~/utilities/enums/ApiParam";
import { ETypoColor } from "~/utilities/enums/Colors";
import { ESort } from "~/utilities/enums/SearchParams";
import { ETypoTag, ETypoVariant } from "~/utilities/enums/Typo";
import { EUnitColumn, EUnitTransactionStatus } from "~/utilities/enums/Unit";
import formatCurrencyPSFRange from "~/utilities/helpers/formatCurrencyPSFRange";
import formatCurrencyRange from "~/utilities/helpers/formatCurrencyRange";
import processStringPrice from "~/utilities/helpers/processStringPrice";

type TPriceListTableProps = {
  isLoading: boolean;
  wrapperClass?: string;
};

const PriceListTable: React.FC<TPriceListTableProps> = ({
  isLoading,
  wrapperClass,
}) => {
  const tableTranslator = "price_list_units";
  const { t } = useTranslation();

  const { project, unitPricesPagination: unitPricesPaginationData } =
    useLoaderData<TProjectPriceListLoaderData & TProjectLoaderData>();

  const [unitPricesPagination, setUnitPricesPagination] =
    useState<IPaginateResponseData<IUnitPrice> & {
      [EApiParam.SORT]?: ESort;
      [EApiParam.SORT_BY]?: EUnitColumn;
    }>();

  const fetcher = useFetcher<TProjectPriceListLoaderData>();

  useEffect(() => {
    setUnitPricesPagination(unitPricesPaginationData);
  }, [unitPricesPaginationData]);

  useEffect(() => {
    if (fetcher.data?.priceList) {
      if (
        unitPricesPagination?.currentPage &&
        fetcher.data.priceList.currentPage > unitPricesPagination.currentPage
      ) {
        const updatedUnitPricesPagination = {
          ...unitPricesPagination,
          items: [
            ...(unitPricesPagination.items || []),
            ...fetcher.data.priceList.items,
          ],
          currentPage: fetcher.data.priceList.currentPage,
          limit: fetcher.data.priceList.limit,
          sort: fetcher.data.priceList.sort,
          sortBy: fetcher.data.priceList.sortBy,
        };
        setUnitPricesPagination(updatedUnitPricesPagination);
      }
    }
  }, [fetcher.data?.priceList, unitPricesPagination]);
  const onLoadMore = useCallback(() => {
    if (
      fetcher.state === "loading" ||
      (typeof fetcher?.data?.priceList?.currentPage === "number" &&
        typeof fetcher?.data?.priceList?.totalPage === "number" &&
        fetcher?.data?.priceList?.currentPage ===
          fetcher?.data?.priceList?.totalPage) ||
      (typeof unitPricesPagination?.currentPage === "number" &&
        typeof unitPricesPagination?.totalPage === "number" &&
        unitPricesPagination?.currentPage === unitPricesPagination?.totalPage)
    )
      return;

    const page = Number(unitPricesPagination?.currentPage) + 1;
    const limit = Number(unitPricesPagination?.limit);
    const sort = Number(unitPricesPagination?.sort);
    const sortBy = Number(unitPricesPagination?.sortBy);
    
    const query = `/loader-project-price-list?${EApiParam.PROJECT_SLUG}=${project?.slug}&${EApiParam.PAGE}=${page}&${EApiParam.LIMIT}=${limit}&${EApiParam.SORT}=sort&${EApiParam.SORT_BY}=${sortBy}`;
    fetcher.load(query);
  }, [
    fetcher,
    project?.slug,
    unitPricesPagination?.currentPage,
    unitPricesPagination?.limit,
    unitPricesPagination?.totalPage,
  ]);

  const cellClass = (row?: Row) =>
    row?.unitStatus !== EUnitTransactionStatus.AVAILABLE
      ? "bg-platinum opacity-50"
      : "bg-tableHeadingBackground";

  const renderTypoCell = (
    content: string | number,
    row?: Row,
    className?: string
  ) => (
    <Typo
      variant={ETypoVariant.BODY_TITLE_16}
      tag={ETypoTag.P}
      color={
        row?.unitStatus === EUnitTransactionStatus.AVAILABLE
          ? ETypoColor.TABLE_HEADING_TEXT
          : ETypoColor.TABLE_TEXT
      }
      className={cn(
        "whitespace-nowrap",
        row?.unitStatus !== EUnitTransactionStatus.AVAILABLE && "opacity-50",
        className
      )}
    >
      {content}
    </Typo>
  );

  const COLUMNS: Column[] = useMemo(
    () => [
      {
        field: "unitStatus",
        headerName: t(`${tableTranslator}.unitStatus`),
        headerCellClassName:
          "!text-header-16 !font-bold first:rounded-none last:rounded-none",
        cellClassName: cellClass,
        renderCell: (row) =>
          renderTypoCell(row?.unitStatus, row, "first-letter:uppercase"),
      },
      {
        field: "unitName",
        headerName: t(`${tableTranslator}.unitName`),
        headerCellClassName:
          "!text-header-16 !font-bold first:rounded-none last:rounded-none",
        cellClassName: cellClass,
        renderCell: (row) => renderTypoCell(row?.unitName, row),
      },
      {
        field: "floorPlanArea",
        headerName: t(`${tableTranslator}.floorPlanArea`),
        headerCellClassName:
          "!text-header-16 !font-bold first:rounded-none last:rounded-none",
        cellClassName: cellClass,
        renderCell: (row) => renderTypoCell(row?.floorPlanArea, row),
      },
      {
        field: "unitPrice",
        headerName: t(`${tableTranslator}.unitPrice`),
        headerCellClassName:
          "!text-header-16 !font-bold first:rounded-none last:rounded-none",
        cellClassName: cellClass,
        renderCell: (row) =>
          renderTypoCell(formatCurrencyRange(row?.unitPrice, null), row),
      },
      {
        field: "bedRoomCount",
        headerName: t(`${tableTranslator}.bedRoomCount`),
        headerCellClassName:
          "!text-header-16 !font-bold first:rounded-none last:rounded-none",
        cellClassName: cellClass,
        renderCell: (row) => renderTypoCell(row?.bedRoomCount, row),
      },
      {
        field: "pricePsf",
        headerName: t(`${tableTranslator}.pricePsf`),
        headerCellClassName:
          "!text-header-16 !font-bold first:rounded-none last:rounded-none",
        cellClassName: cellClass,
        renderCell: (row) =>
          renderTypoCell(formatCurrencyPSFRange(row?.minPSF, row?.maxPSF), row),
      },
      {
        field: "floorPlanName",
        headerName: t(`${tableTranslator}.floorPlanName`),
        headerCellClassName:
          "!text-header-16 !font-bold first:rounded-none last:rounded-none",
        cellClassName: cellClass,
        renderCell: (row) =>
          renderTypoCell(processStringPrice(row?.floorPlanName), row),
      },
      {
        field: "unitTypeTitle",
        headerName: t(`${tableTranslator}.unitTypeTitle`),
        headerCellClassName:
          "!text-header-16 !font-bold first:rounded-none last:rounded-none",
        cellClassName: cellClass,
        renderCell: (row) =>
          renderTypoCell(processStringPrice(row?.unitTypeTitle), row),
      },
    ],
    [t]
  );

  return (
    <HorizontalTable
      columns={COLUMNS}
      rows={unitPricesPagination?.items || []}
      page={Number(unitPricesPagination?.page || PAGE)}
      onLoadMore={() => onLoadMore()}
      infinitiLoad
      isLoading={isLoading}
      stickyRows
      classNames={{
        header:
          "bg-tableHeadingText text-tableHeadingBackground first:rounded-none last:rounded-none",
        wrapper: wrapperClass,
      }}
    />
  );
};

export default PriceListTable;
